<script setup lang="ts">
import { ref, nextTick, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useTabStore } from '@/stores/tabs'
import { X, Pin, PinOff, Plus, Pencil } from 'lucide-vue-next'

const emit = defineEmits<{ openSearch: [] }>()

const router = useRouter()
const tabStore = useTabStore()
const scrollContainer = ref<HTMLElement | null>(null)
const contextMenu = ref<{ show: boolean; x: number; y: number; tabId: string }>({
  show: false, x: 0, y: 0, tabId: '',
})
const renaming = ref<{ tabId: string; value: string } | null>(null)
const renameInputRef = ref<HTMLInputElement | null>(null)

// Drag and drop
const dragTabId = ref<string | null>(null)
const dragOverTabId = ref<string | null>(null)

function handleDragStart(e: DragEvent, tab: { id: string }) {
  dragTabId.value = tab.id
  if (e.dataTransfer) {
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', tab.id)
  }
  // Add a slight delay so the drag image captures properly
  const target = e.target as HTMLElement
  target.classList.add('dragging')
}

function handleDragOver(e: DragEvent, tab: { id: string }) {
  e.preventDefault()
  if (e.dataTransfer) e.dataTransfer.dropEffect = 'move'
  if (dragTabId.value && dragTabId.value !== tab.id) {
    dragOverTabId.value = tab.id
  }
}

function handleDragLeave(_e: DragEvent, tab: { id: string }) {
  if (dragOverTabId.value === tab.id) {
    dragOverTabId.value = null
  }
}

function handleDrop(e: DragEvent, tab: { id: string }) {
  e.preventDefault()
  if (!dragTabId.value || dragTabId.value === tab.id) return
  const fromIndex = tabStore.tabs.findIndex(t => t.id === dragTabId.value)
  const toIndex = tabStore.tabs.findIndex(t => t.id === tab.id)
  tabStore.moveTab(fromIndex, toIndex)
  dragTabId.value = null
  dragOverTabId.value = null
}

function handleDragEnd(e: DragEvent) {
  const target = e.target as HTMLElement
  target.classList.remove('dragging')
  dragTabId.value = null
  dragOverTabId.value = null
}

function handleTabClick(tab: { id: string; path: string }) {
  tabStore.setActiveTab(tab.id)
  router.push(tab.path)
}

function handleTabClose(e: Event, tabId: string) {
  e.stopPropagation()
  const navigateTo = tabStore.closeTab(tabId)
  if (navigateTo) router.push(navigateTo)
}

function handleMiddleClick(e: MouseEvent, tabId: string) {
  if (e.button === 1) {
    e.preventDefault()
    const navigateTo = tabStore.closeTab(tabId)
    if (navigateTo) router.push(navigateTo)
  }
}

function handleContextMenu(e: MouseEvent, tabId: string) {
  e.preventDefault()
  contextMenu.value = { show: true, x: e.clientX, y: e.clientY, tabId }
}

function closeContextMenu() {
  contextMenu.value.show = false
}

function ctxClose() {
  const navigateTo = tabStore.closeTab(contextMenu.value.tabId)
  if (navigateTo) router.push(navigateTo)
  closeContextMenu()
}

function ctxCloseOthers() {
  tabStore.closeOtherTabs(contextMenu.value.tabId)
  const tab = tabStore.tabs.find(t => t.id === contextMenu.value.tabId)
  if (tab) {
    tabStore.setActiveTab(tab.id)
    router.push(tab.path)
  }
  closeContextMenu()
}

function ctxCloseAll() {
  const navigateTo = tabStore.closeAllTabs()
  router.push(navigateTo)
  closeContextMenu()
}

function startRename(tabId: string, currentTitle: string) {
  renaming.value = { tabId, value: currentTitle }
  nextTick(() => {
    renameInputRef.value?.focus()
    renameInputRef.value?.select()
  })
}

function ctxRename() {
  const tab = tabStore.tabs.find(t => t.id === contextMenu.value.tabId)
  if (tab) {
    closeContextMenu()
    startRename(tab.id, tab.title)
  }
}

function commitRename() {
  if (renaming.value && renaming.value.value.trim()) {
    tabStore.updateTabTitle(renaming.value.tabId, renaming.value.value.trim())
  }
  renaming.value = null
}

function cancelRename() {
  renaming.value = null
}

function ctxDuplicate() {
  const tab = tabStore.tabs.find(t => t.id === contextMenu.value.tabId)
  if (tab) {
    tabStore.addTab(tab.path, tab.title, { duplicate: true })
    router.push(tab.path)
  }
  closeContextMenu()
}

function ctxPin() {
  const tab = tabStore.tabs.find(t => t.id === contextMenu.value.tabId)
  if (tab) {
    if (tab.pinned) tabStore.unpinTab(tab.id)
    else tabStore.pinTab(tab.id)
  }
  closeContextMenu()
}

// Scroll active tab into view
function scrollActiveIntoView() {
  nextTick(() => {
    const container = scrollContainer.value
    if (!container) return
    const activeEl = container.querySelector('[data-tab-active="true"]') as HTMLElement
    if (activeEl) {
      activeEl.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' })
    }
  })
}

// Close context menu on click outside
function handleDocClick() {
  contextMenu.value.show = false
}

onMounted(() => {
  document.addEventListener('click', handleDocClick)
  scrollActiveIntoView()
})

onUnmounted(() => {
  document.removeEventListener('click', handleDocClick)
})
</script>

<template>
  <div v-if="tabStore.tabCount > 0" class="tab-bar">
    <div ref="scrollContainer" class="tab-scroll-container">
      <div
        v-for="tab in tabStore.tabs"
        :key="tab.id"
        class="tab-item"
        :class="{
          active: tab.id === tabStore.activeTabId,
          pinned: tab.pinned,
          'has-update': tab.hasUpdate,
          'drag-over': dragOverTabId === tab.id,
        }"
        :data-tab-active="tab.id === tabStore.activeTabId"
        :title="tab.title"
        draggable="true"
        @dragstart="handleDragStart($event, tab)"
        @dragover="handleDragOver($event, tab)"
        @dragleave="handleDragLeave($event, tab)"
        @drop="handleDrop($event, tab)"
        @dragend="handleDragEnd($event)"
        @click="handleTabClick(tab)"
        @mousedown="handleMiddleClick($event, tab.id)"
        @contextmenu="handleContextMenu($event, tab.id)"
      >
        <span v-if="tab.hasUpdate" class="tab-update-dot" />
        <Pin v-if="tab.pinned" class="w-3 h-3 text-primary-400 flex-shrink-0" />
        <input
          v-if="renaming && renaming.tabId === tab.id"
          ref="renameInputRef"
          v-model="renaming.value"
          class="tab-rename-input"
          @keydown.enter.prevent="commitRename"
          @keydown.escape.prevent="cancelRename"
          @blur="commitRename"
          @click.stop
        />
        <span v-else class="tab-title" @dblclick.stop="startRename(tab.id, tab.title)">{{ tab.title }}</span>
        <button
          v-if="tab.closable"
          class="tab-close"
          @click="handleTabClose($event, tab.id)"
          title="Close tab"
        >
          <X class="w-3 h-3" />
        </button>
      </div>
      <!-- New Tab button at the end -->
      <button
        class="tab-new"
        @click="emit('openSearch')"
        title="Open new tab (Ctrl+K)"
      >
        <Plus class="w-4.5 h-4.5" />
      </button>
    </div>

    <!-- Context Menu -->
    <Teleport to="body">
      <div
        v-if="contextMenu.show"
        class="tab-context-menu"
        :style="{ left: contextMenu.x + 'px', top: contextMenu.y + 'px' }"
        @click.stop
      >
        <button @click="ctxRename" class="tab-ctx-item">
          <Pencil class="w-3.5 h-3.5 mr-2" /> Rename Tab
        </button>
        <button @click="ctxDuplicate" class="tab-ctx-item">
          <Plus class="w-3.5 h-3.5 mr-2" /> Duplicate Tab
        </button>
        <div class="tab-ctx-divider" />
        <button @click="ctxClose" class="tab-ctx-item">Close</button>
        <button @click="ctxCloseOthers" class="tab-ctx-item">Close Others</button>
        <button @click="ctxCloseAll" class="tab-ctx-item">Close All</button>
        <div class="tab-ctx-divider" />
        <button @click="ctxPin" class="tab-ctx-item">
          <template v-if="tabStore.tabs.find(t => t.id === contextMenu.tabId)?.pinned">
            <PinOff class="w-3.5 h-3.5 mr-2" /> Unpin
          </template>
          <template v-else>
            <Pin class="w-3.5 h-3.5 mr-2" /> Pin Tab (opens on login)
          </template>
        </button>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
.tab-bar {
  @apply sticky top-[57px] z-20 bg-surface-900 border-b border-surface-800;
  min-height: 44px;
}

.tab-scroll-container {
  @apply flex items-center overflow-x-auto gap-0;
  scrollbar-width: none;
  -ms-overflow-style: none;
}
.tab-scroll-container::-webkit-scrollbar {
  display: none;
}

.tab-item {
  @apply flex items-center gap-2 px-4 py-2.5 text-sm text-surface-400 cursor-pointer
         border-r border-surface-800 whitespace-nowrap select-none
         hover:bg-surface-800 hover:text-surface-200 transition-colors;
  min-width: 120px;
  max-width: 240px;
}

.tab-item.active {
  @apply bg-surface-800/80 text-surface-100;
  box-shadow: inset 0 -2px 0 var(--accent-primary, #06b6d4);
}

.tab-item.pinned {
  min-width: auto;
}

.tab-title {
  @apply truncate font-semibold;
}

.tab-close {
  @apply flex-shrink-0 p-0.5 rounded text-surface-500 hover:text-surface-100 hover:bg-surface-700 transition-colors opacity-0;
}
.tab-item:hover .tab-close,
.tab-item.active .tab-close {
  opacity: 1;
}

.tab-context-menu {
  @apply fixed z-[9999] py-1 bg-surface-800 border border-surface-700 rounded-lg shadow-lg min-w-[160px];
}

.tab-ctx-item {
  @apply flex items-center w-full px-3 py-1.5 text-xs text-surface-300 hover:bg-surface-700 hover:text-surface-100 transition-colors;
}

.tab-ctx-divider {
  @apply my-1 border-t border-surface-700;
}

.tab-new {
  @apply flex-shrink-0 flex items-center justify-center w-9 h-9 my-0.5 mx-1
         text-surface-500 hover:text-surface-200 hover:bg-surface-800
         rounded transition-colors cursor-pointer;
}

/* Inline rename */
.tab-rename-input {
  @apply bg-surface-700 text-surface-100 text-xs border border-primary-500 rounded px-1.5 py-0.5 outline-none;
  width: 120px;
  max-width: 160px;
}

/* Drag and drop */
.tab-item[draggable="true"] {
  cursor: grab;
}
.tab-item.dragging {
  opacity: 0.4;
  cursor: grabbing;
}
.tab-item.drag-over {
  box-shadow: inset 2px 0 0 0 var(--accent-primary, #06b6d4);
}

/* Update notification dot */
.tab-update-dot {
  @apply flex-shrink-0 w-2 h-2 rounded-full bg-primary-500;
  animation: tab-pulse 2s ease-in-out infinite;
  box-shadow: 0 0 6px var(--accent-primary, #06b6d4);
}

@keyframes tab-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}

/* Subtle glow on the tab text when updated */
.tab-item.has-update .tab-title {
  @apply text-primary-400;
}
</style>
